/** The coarse `user_agent_class` enum carried by `site.session.started` (§7.2 / I5). */
export type UserAgentClass = 'bot-suspect' | 'desktop' | 'mobile';

/** Substrings that mark a UA as an automated crawler/agent (lower-cased match). MUST stay alphabetized. */
const BOT_MARKERS = [
  'bot',
  'crawl',
  'headless',
  'http',
  'lighthouse',
  'python',
  'scan',
  'slurp',
  'spider',
  'wget'
] as const;

/** Substrings that mark a UA as a handheld/mobile browser (lower-cased match). MUST stay alphabetized. */
const MOBILE_MARKERS = ['android', 'iphone', 'ipod', 'mobile', 'windows phone'] as const;

export interface IClassifyUserAgent {
  fromUserAgent(userAgent: string): UserAgentClass;
}

/**
 * Pure classifier collapsing a raw UA string into the coarse `user_agent_class` enum
 * (`desktop | mobile | bot-suspect`) that `site.session.started` carries — never the raw UA (§7.2).
 * The `BeaconValidator` rejects anything outside this enum, so this MUST return one of the three.
 * Order matters: bots first (a headless Chrome also matches "mobile"-ish tokens), then mobile, then
 * desktop as the default. Missing/empty UA is treated as `bot-suspect` (a real browser always sends one).
 */
export const ClassifyUserAgent: IClassifyUserAgent = {
  /**
   * Classify a raw UA string.
   * @param userAgent - The `navigator.userAgent` string (may be empty).
   * @returns One of `bot-suspect | desktop | mobile`.
   */
  fromUserAgent(userAgent) {
    const ua = userAgent.trim().toLowerCase();
    if (ua.length === 0) return 'bot-suspect';
    if (BOT_MARKERS.some(marker => ua.includes(marker))) return 'bot-suspect';
    if (MOBILE_MARKERS.some(marker => ua.includes(marker))) return 'mobile';
    return 'desktop';
  }
};
